// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {
  useResponsive,
  useEffect,
  useMemo,
  useToast,
  ResponsiveType,
  MutationError,
} from '@supermove/hooks';
import {OrganizationModel, UserModel} from '@supermove/models';
import {Typography, colors} from '@supermove/styles';

// App
import ProgressModal from '@shared/design/components/ProgressModal';
import Text from '@shared/design/components/Text';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import useProgress from '@shared/modules/App/hooks/useProgress';
import CallrailAccountAndCompanyFields from '@shared/modules/CallrailIntegration/components/CallrailAccountAndCompanyFields';
import CallrailApiKeyFields from '@shared/modules/CallrailIntegration/components/CallrailApiKeyFields';
import CallrailExcludedPhoneNumbersFields from '@shared/modules/CallrailIntegration/components/CallrailExcludedPhoneNumbersFields';
import CallrailProjectSettingsFields from '@shared/modules/CallrailIntegration/components/CallrailProjectSettingsFields';
import CallrailReviewFields from '@shared/modules/CallrailIntegration/components/CallrailReviewFields';
import SetupCallrailIntegrationModalFooter from '@shared/modules/CallrailIntegration/components/SetupCallrailIntegrationModalFooter';
import CallrailIntegrationMode from '@shared/modules/CallrailIntegration/enums/CallrailIntegrationMode';
import SetupCallrailIntegrationStep, {
  Step,
} from '@shared/modules/CallrailIntegration/enums/SetupCallrailIntegrationStep';
import SetupCallrailIntegrationForm, {
  FormikSetupCallrailIntegrationFormType,
} from '@shared/modules/CallrailIntegration/forms/SetupCallrailIntegrationForm';
import useSetupCallrailIntegrationMutation from '@shared/modules/CallrailIntegration/hooks/useSetupCallrailIntegrationMutation';

interface MobilePaddingContainerProps {
  responsive: ResponsiveType;
}

const MobilePaddingContainer = Styled.View<MobilePaddingContainerProps>`
  padding-horizontal: ${({responsive}) => (responsive.desktop ? 0 : 16)}px;
`;

const StepHeaderDescription = Styled.Text`
  ${Typography.Responsive.Body}
  color: ${colors.gray.secondary};
`;

const STEPS = SetupCallrailIntegrationStep.STEPS.map((step: Step) => ({
  kind: step.kind,
}));

interface StepFieldsProps {
  stepKind: string;
  form: FormikSetupCallrailIntegrationFormType;
  field: string;
  setCurrentStepKind: (value: string | ((prevVar: string) => string)) => void;
  responsive: ResponsiveType;
  organization: OrganizationModel;
}

const StepFields = ({
  stepKind,
  form,
  field,
  setCurrentStepKind,
  responsive,
  organization,
}: StepFieldsProps) => {
  switch (stepKind) {
    case SetupCallrailIntegrationStep.API_KEY:
      return (
        <MobilePaddingContainer responsive={responsive}>
          <CallrailApiKeyFields form={form} field={field} />
        </MobilePaddingContainer>
      );
    case SetupCallrailIntegrationStep.ACCOUNT_AND_COMPANY:
      return (
        <MobilePaddingContainer responsive={responsive}>
          <CallrailAccountAndCompanyFields form={form} field={field} />
        </MobilePaddingContainer>
      );
    case SetupCallrailIntegrationStep.PROJECT_SETTINGS:
      return (
        <MobilePaddingContainer responsive={responsive}>
          <CallrailProjectSettingsFields form={form} field={field} organization={organization} />
        </MobilePaddingContainer>
      );
    case SetupCallrailIntegrationStep.EXCLUDED_PHONE_NUMBERS:
      return (
        <MobilePaddingContainer responsive={responsive}>
          <CallrailExcludedPhoneNumbersFields form={form} field={field} />
        </MobilePaddingContainer>
      );
    case SetupCallrailIntegrationStep.REVIEW:
      return (
        <CallrailReviewFields
          form={form}
          field={field}
          setCurrentStepKind={setCurrentStepKind}
          organization={organization}
        />
      );
    default:
      return null;
  }
};

interface SetupCallrailIntegrationModalBodyProps {
  form: FormikSetupCallrailIntegrationFormType;
  field: string;
  currentStep: Step;
  currentStepKind: string;
  setCurrentStepKind: (value: string | ((prevVar: string) => string)) => void;
  organization: OrganizationModel;
}

const SetupCallrailIntegrationModalBody = ({
  form,
  field,
  currentStep,
  currentStepKind,
  setCurrentStepKind,
  organization,
}: SetupCallrailIntegrationModalBodyProps) => {
  const responsive = useResponsive();

  return (
    <React.Fragment>
      <MobilePaddingContainer responsive={responsive}>
        <Text.Heading2>{currentStep.title}</Text.Heading2>
        <Space height={8} />
        <Text.Body style={{color: colors.gray.secondary}}>{currentStep.description}</Text.Body>
      </MobilePaddingContainer>
      <Space height={16} />
      <StepFields
        stepKind={currentStepKind}
        form={form}
        field={field}
        setCurrentStepKind={setCurrentStepKind}
        responsive={responsive}
        organization={organization}
      />
      {responsive.desktop && <Space height={24} />}
    </React.Fragment>
  );
};

interface SetupCallrailIntegrationModalProps {
  isOpen: boolean;
  handleClose: () => void;
  refetch: () => void;
  viewer: UserModel;
}

const SetupCallrailIntegrationModal = ({
  isOpen,
  handleClose,
  refetch,
  viewer,
}: SetupCallrailIntegrationModalProps) => {
  const successToast = useToast({
    ToastComponent: SuccessToast,
    message: 'CallRail integration created successfully.',
  });

  const setupCallrailIntegrationForm = useMemo(
    () =>
      SetupCallrailIntegrationForm.new({
        organizationId: viewer.viewingOrganization.id,
      }),
    [viewer],
  );

  const {form, handleSubmit, submitting} = useSetupCallrailIntegrationMutation({
    setupCallrailIntegrationForm,
    onSuccess: () => {
      handleClose();
      refetch();
      successToast.handleToast();
    },
    onError: (errors: MutationError[]) => {
      console.log(errors);
    },
  });

  // Automatically set the mode based on sales app access
  const {hasActiveSalesProduct} = viewer.viewingOrganization;
  useEffect(() => {
    if (form.values.setupCallrailIntegrationForm) {
      const mode = hasActiveSalesProduct
        ? CallrailIntegrationMode.ENHANCE_CALLS
        : CallrailIntegrationMode.CREATE_CALLS;
      form.setFieldValue('setupCallrailIntegrationForm.mode', mode);
    }
  }, [form, hasActiveSalesProduct]);

  // Filter steps based on integration mode - include EXCLUDED_PHONE_NUMBERS only for CREATE_CALLS mode
  const filteredSteps = useMemo(() => {
    const mode = form.values.setupCallrailIntegrationForm?.mode;

    if (mode === CallrailIntegrationMode.ENHANCE_CALLS) {
      return SetupCallrailIntegrationStep.STEPS.filter(
        (step) =>
          step.kind !== SetupCallrailIntegrationStep.PROJECT_SETTINGS &&
          step.kind !== SetupCallrailIntegrationStep.EXCLUDED_PHONE_NUMBERS,
      );
    }

    return SetupCallrailIntegrationStep.STEPS;
  }, [form.values.setupCallrailIntegrationForm?.mode]);

  // Create custom navigation functions that skip PROJECT_SETTINGS for ENHANCE_CALLS mode
  const getCustomPreviousStepKind = (kind: string) => {
    const mode = form.values.setupCallrailIntegrationForm?.mode;

    // For ENHANCE_CALLS mode, when on REVIEW step, go back to ACCOUNT_AND_COMPANY (skip PROJECT_SETTINGS)
    if (
      mode === CallrailIntegrationMode.ENHANCE_CALLS &&
      kind === SetupCallrailIntegrationStep.REVIEW
    ) {
      return SetupCallrailIntegrationStep.ACCOUNT_AND_COMPANY;
    }

    return SetupCallrailIntegrationStep.getPreviousStepKind(kind, filteredSteps);
  };

  const getCustomNextStepKind = (kind: string) => {
    const mode = form.values.setupCallrailIntegrationForm?.mode;

    // For ENHANCE_CALLS mode, when on ACCOUNT_AND_COMPANY step, go directly to REVIEW (skip PROJECT_SETTINGS)
    if (
      mode === CallrailIntegrationMode.ENHANCE_CALLS &&
      kind === SetupCallrailIntegrationStep.ACCOUNT_AND_COMPANY
    ) {
      return SetupCallrailIntegrationStep.REVIEW;
    }

    return SetupCallrailIntegrationStep.getNextStepKind(kind, filteredSteps);
  };

  const {
    currentStepKind,
    setCurrentStepKind,
    isViewingFirstStep,
    previousStepKind,
    nextStepKind,
    completedSteps,
    setCompletedSteps,
    allStepKinds,
    nextStepToComplete,
    currentStepIndex,
  } = useProgress({
    steps: filteredSteps,
    getPreviousStepKind: getCustomPreviousStepKind,
    getNextStepKind: getCustomNextStepKind,
  });

  const currentStep = useMemo(
    () => SetupCallrailIntegrationStep.getStep(currentStepKind),
    [currentStepKind],
  );

  const isReviewingSteps = useMemo(
    () =>
      _.isEqual(
        _.sortBy(
          allStepKinds.filter((stepKind) => stepKind !== SetupCallrailIntegrationStep.REVIEW),
        ),
        _.sortBy(completedSteps),
      ),
    [allStepKinds, completedSteps],
  );

  return (
    <ProgressModal
      isOpen={isOpen}
      handleClose={handleClose}
      steps={filteredSteps}
      completedSteps={completedSteps}
      isViewingFirstStep={isViewingFirstStep}
      currentStepKind={currentStepKind}
      getStepTitle={(kind) =>
        (_.find(SetupCallrailIntegrationStep.STEPS, (step) => step.kind === kind) || {title: ''})
          .title
      }
      setCurrentStepKind={setCurrentStepKind}
      nextStepToComplete={nextStepToComplete}
      currentStepIndex={currentStepIndex}
      title={'Setup CallRail Integration'}
      handleBack={
        isViewingFirstStep
          ? null
          : () => {
              previousStepKind && setCurrentStepKind(previousStepKind);
            }
      }
      BodyElement={
        <SetupCallrailIntegrationModalBody
          form={form}
          field={'setupCallrailIntegrationForm'}
          currentStep={currentStep}
          currentStepKind={currentStepKind}
          setCurrentStepKind={setCurrentStepKind}
          organization={viewer.viewingOrganization}
        />
      }
      FooterElement={
        <SetupCallrailIntegrationModalFooter
          isViewingFirstStep={isViewingFirstStep}
          previousStepKind={previousStepKind || undefined}
          nextStepKind={nextStepKind || undefined}
          currentStepKind={currentStepKind}
          setCurrentStepKind={setCurrentStepKind}
          setCompletedSteps={setCompletedSteps}
          form={form}
          isReviewingSteps={isReviewingSteps}
          handleSubmit={handleSubmit}
          submitting={submitting}
          getStep={(stepKind) => SetupCallrailIntegrationStep.getStep(stepKind)}
        />
      }
    />
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
SetupCallrailIntegrationModal.fragment = gql`
  ${CallrailProjectSettingsFields.fragment}
  ${CallrailReviewFields.fragment}

  fragment SetupCallrailIntegrationModal on User {
    id
    viewingOrganization {
      id
      hasActiveSalesProduct: hasActiveSupermoveProduct(kind: "SALES")
      ...CallrailProjectSettingsFields
      ...CallrailReviewFields
    }
  }
`;

export default SetupCallrailIntegrationModal;
